import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { clearAllFilters as clearAllFiltersUtil, getSelectedFiltersCount } from "@faire/retailer-visitor-shared/components/Filters/FilterUtils";
import { useFilterSectionSelectedChange } from "@faire/retailer-visitor-shared/components/Filters/useFilterSectionSelectedChange";
import { useSettingMillionLintFiltersOptimization } from "@faire/retailer-visitor-shared/settings/useSettingMillionLintFiltersOptimization";
import { useRefFrom } from "@faire/web--source/ui/hooks/useRefFrom";
import { useRef } from "react";
export var FilterState;
(function(FilterState) {
    FilterState.build = function(partial) {
        var _partial_filterSections, _partial_isNewFilterSelected, _partial_isSideBarVisible, _partial_isMobileModalOpen, _partial_shouldScrollToResults, _partial_activeFilterCount, _partial_shouldShowFilterCollapsePill, _partial_hideOptionsResultCount, _partial_filterBarScrollTop, _partial_preserveScrollPosition;
        return {
            filterSections: (_partial_filterSections = partial === null || partial === void 0 ? void 0 : partial.filterSections) !== null && _partial_filterSections !== void 0 ? _partial_filterSections : [],
            isNewFilterSelected: (_partial_isNewFilterSelected = partial === null || partial === void 0 ? void 0 : partial.isNewFilterSelected) !== null && _partial_isNewFilterSelected !== void 0 ? _partial_isNewFilterSelected : false,
            isSideBarVisible: (_partial_isSideBarVisible = partial === null || partial === void 0 ? void 0 : partial.isSideBarVisible) !== null && _partial_isSideBarVisible !== void 0 ? _partial_isSideBarVisible : true,
            isMobileModalOpen: (_partial_isMobileModalOpen = partial === null || partial === void 0 ? void 0 : partial.isMobileModalOpen) !== null && _partial_isMobileModalOpen !== void 0 ? _partial_isMobileModalOpen : false,
            shouldScrollToResults: (_partial_shouldScrollToResults = partial === null || partial === void 0 ? void 0 : partial.shouldScrollToResults) !== null && _partial_shouldScrollToResults !== void 0 ? _partial_shouldScrollToResults : true,
            activeFilterCount: (_partial_activeFilterCount = partial === null || partial === void 0 ? void 0 : partial.activeFilterCount) !== null && _partial_activeFilterCount !== void 0 ? _partial_activeFilterCount : (partial === null || partial === void 0 ? void 0 : partial.filterSections) ? getSelectedFiltersCount(partial.filterSections) : -1,
            shouldShowFilterCollapsePill: (_partial_shouldShowFilterCollapsePill = partial === null || partial === void 0 ? void 0 : partial.shouldShowFilterCollapsePill) !== null && _partial_shouldShowFilterCollapsePill !== void 0 ? _partial_shouldShowFilterCollapsePill : false,
            hideOptionsResultCount: (_partial_hideOptionsResultCount = partial === null || partial === void 0 ? void 0 : partial.hideOptionsResultCount) !== null && _partial_hideOptionsResultCount !== void 0 ? _partial_hideOptionsResultCount : false,
            totalPrimaryResultsCount: partial === null || partial === void 0 ? void 0 : partial.totalPrimaryResultsCount,
            totalPrimaryResultsCountFormatted: partial === null || partial === void 0 ? void 0 : partial.totalPrimaryResultsCountFormatted,
            filterBarScrollTop: (_partial_filterBarScrollTop = partial === null || partial === void 0 ? void 0 : partial.filterBarScrollTop) !== null && _partial_filterBarScrollTop !== void 0 ? _partial_filterBarScrollTop : 0,
            preserveScrollPosition: (_partial_preserveScrollPosition = partial === null || partial === void 0 ? void 0 : partial.preserveScrollPosition) !== null && _partial_preserveScrollPosition !== void 0 ? _partial_preserveScrollPosition : false
        };
    };
})(FilterState || (FilterState = {}));
/**
 * Creates a filter store from a ref callback.
 *
 * @param initialState - The initial state of the filter store.
 * @param onFilterSelectedRef - A mutable ref object that holds a callback function to be called when a filter is selected.
 * @returns The filter store object with getState, setState, subscribe, and clearAllFilters methods.
 */ export var createFilterStore = function(initialState, onFilterSelectedRef, shouldMemoMillionLint) {
    var currentState = initialState;
    var subscribers = new Map();
    var getState = function() {
        return currentState;
    };
    var setState = function(selector) {
        var partialStore = typeof selector === "function" ? selector(currentState) : selector;
        if (partialStore.filterSections !== undefined) {
            partialStore.activeFilterCount = getSelectedFiltersCount(partialStore.filterSections);
        }
        var changedFields = Object.keys(partialStore).filter(function(field) {
            if (shouldMemoMillionLint) {
                /**
                 * Compare filterSections with shallow to correctly check if the contents of filterSections have changed.
                 * We can't use === to compare filterSections because it's an array
                 * The rest of the fields are primitives so we can use === to compare them.
                 */ if (field === "filterSections") {
                    return JSON.stringify(currentState[field]) !== JSON.stringify(partialStore[field]);
                }
                return currentState[field] !== partialStore[field];
            } else {
                return currentState[field] !== partialStore[field];
            }
        });
        if (changedFields.length === 0) {
            return;
        }
        var prevFilterSections = currentState.filterSections;
        currentState = Object.assign({}, currentState, partialStore);
        /**
         * Prevent re-renders due to filterSections reference changing but the value being the same.
         * Since we are creating a shallow copy of the filterSections, the reference will be different.
         * But the value is the same, so we don't want to re-render in that case.
         * i.e When isNewFilterSelected changes from true to false, we don't want to re-render everything depending on the filterSections.
         */ if (shouldMemoMillionLint) {
            if (!partialStore.filterSections || partialStore.filterSections === prevFilterSections || !changedFields.includes("filterSections")) {
                currentState.filterSections = prevFilterSections;
            }
        }
        if (currentState.isNewFilterSelected) {
            var _onFilterSelectedRef_current;
            (_onFilterSelectedRef_current = onFilterSelectedRef.current) === null || _onFilterSelectedRef_current === void 0 ? void 0 : _onFilterSelectedRef_current.call(onFilterSelectedRef, currentState);
        }
        // Notify consumers that use the changed fields
        changedFields.forEach(function(field) {
            var fieldSubscribers = subscribers.get(field);
            if (fieldSubscribers) {
                fieldSubscribers.forEach(function(callback) {
                    return callback();
                });
            }
        });
    };
    var clearAllFilters = function() {
        setState(function(currentState) {
            clearAllFiltersUtil(currentState.filterSections);
            return _object_spread_props(_object_spread({}, currentState), {
                isNewFilterSelected: true
            });
        });
        useFilterSectionSelectedChange.getState().optionChanged(shouldMemoMillionLint !== null && shouldMemoMillionLint !== void 0 ? shouldMemoMillionLint : false);
    };
    var subscribe = function(callback, fields) {
        // Add the callback to the subscribers for each field
        fields.forEach(function(field) {
            var fieldSubscribers = subscribers.get(field);
            if (fieldSubscribers) {
                fieldSubscribers.add(callback);
            } else {
                subscribers.set(field, new Set([
                    callback
                ]));
            }
        });
        // Return a function to unsubscribe from all fields
        return function() {
            fields.forEach(function(field) {
                var fieldSubscribers = subscribers.get(field);
                if (fieldSubscribers) {
                    var deleted = fieldSubscribers.delete(callback);
                    if (!deleted) {
                        // eslint-disable-next-line no-console
                        console.warn("useStore with fields ".concat(fields, " could not unsubscribe from field ").concat(String(field)));
                    }
                }
            });
        };
    };
    return {
        getState: getState,
        setState: setState,
        subscribe: subscribe,
        clearAllFilters: clearAllFilters
    };
};
export var useCreateFilterStore = function(initialState, onFilterSelected) {
    var shouldMemoMillionLint = useSettingMillionLintFiltersOptimization();
    var selectFilterRef = useRef(onFilterSelected);
    selectFilterRef.current = onFilterSelected;
    return useRefFrom(function() {
        return createFilterStore(initialState, selectFilterRef, shouldMemoMillionLint);
    }).current;
};
